import React, { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { DownloadIcon, SheetIcon, UploadIcon } from "../../components/icons";
import { cn } from "../../components/cn";
import { tallyLedgerApi, type TallyLedgerImport } from "../../data/tallyLedgerApi";
import { tallyGroupLabel } from "../../domain/tallyChart";

export function TallyLedgerImportPanel({
  clientId,
  companyName,
  onChanged,
}: {
  clientId: ClientId;
  companyName: string;
  onChanged?: () => void;
}) {
  const [review, setReview] = useState<TallyLedgerImport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    tallyLedgerApi
      .currentImport(clientId)
      .then((next) => {
        if (liveRef.current) setReview(next);
      })
      .catch(() => undefined);
    return () => {
      liveRef.current = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (review?.import?.status !== "committing") return;
    const timer = window.setInterval(() => {
      tallyLedgerApi
        .currentImport(clientId)
        .then((next) => {
          if (!liveRef.current) return;
          setReview(next);
          if (next.import?.status === "done") onChanged?.();
        })
        .catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [clientId, review?.import?.status, onChanged]);

  function download() {
    setBusy(true);
    setError(null);
    tallyLedgerApi
      .template(clientId)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not build the sheet.");
      })
      .finally(() => setBusy(false));
  }

  function upload(file: File) {
    setBusy(true);
    setError(null);
    tallyLedgerApi
      .upload(clientId, file)
      .then((next) => {
        if (liveRef.current) setReview(next);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not read that sheet.");
      })
      .finally(() => {
        setBusy(false);
        if (inputRef.current) inputRef.current.value = "";
      });
  }

  function discard() {
    setBusy(true);
    tallyLedgerApi
      .cancelImport(clientId)
      .then(() => {
        if (liveRef.current) setReview(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not discard the import.");
      })
      .finally(() => setBusy(false));
  }

  function close() {
    setBusy(true);
    tallyLedgerApi
      .closeImport(clientId)
      .then(() => {
        if (liveRef.current) setReview(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not close the import.");
      })
      .finally(() => setBusy(false));
  }

  function create() {
    setCreating(true);
    setError(null);
    tallyLedgerApi
      .commitImport(clientId)
      .then((next) => {
        if (!liveRef.current) return;
        setReview(next);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not create those ledgers.");
      })
      .finally(() => {
        if (liveRef.current) setCreating(false);
      });
  }

  const summary = review?.import ?? null;
  const rows = (review?.rows ?? []).filter((row) => row.action !== "unchanged");
  const books = `${companyName}'s Tally`;

  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium">Bulk Import Your Ledgers</h3>
          <p className="text-sm text-muted-foreground">
            Download the sheet, add ledgers and groups, and upload it here. A new group can sit under a group
            already in Tally, or under a group row above it. A ledger can sit under any of those groups.
            Parent Group is filled in for the ledgers already read.
          </p>
        </div>
        <CoButton variant="outline" onClick={download} disabled={busy || creating}>
          <DownloadIcon className="size-4" />
          Download sheet
        </CoButton>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      {summary === null ? (
        <label
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            const file = event.dataTransfer.files[0];
            if (file) upload(file);
          }}
          className={cn(
            "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-dashed p-6 text-center transition-colors",
            dragging ? "border-primary bg-primary/5" : "hover:bg-muted/40",
            busy && "pointer-events-none opacity-70",
          )}
        >
          <UploadIcon className="size-5 text-muted-foreground" />
          <span className="text-sm font-medium">
            {busy ? "Reading the sheet…" : "Drop the filled-in sheet here, or click to choose"}
          </span>
          <span className="text-xs text-muted-foreground">.xlsx or .csv, up to 2 MB</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            className="sr-only"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload(file);
            }}
          />
        </label>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <SheetIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="font-medium">{summary.filename ?? "Uploaded sheet"}</span>
            <span className="text-xs text-muted-foreground">{summary.total_rows} rows read</span>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Count label="New" value={summary.new_rows} />
            <Count label="Unchanged" value={summary.unchanged_rows} />
            <Count label="Need a look" value={summary.invalid_rows} />
            <Count
              label={summary.status === "done" || summary.status === "committing" ? "Created" : "Ready"}
              value={summary.status === "done" || summary.status === "committing" ? summary.created_rows : summary.new_rows}
            />
          </div>

          {summary.duplicate_of_commit && (
            <Callout variant="warning">
              This exact file was created in Tally before. Creating it again will try those names again.
            </Callout>
          )}
          {summary.status === "committing" && (
            <p className="text-sm text-muted-foreground">Creating them in Tally…</p>
          )}
          {summary.status === "done" && (
            <p className="text-sm text-muted-foreground">
              Created {summary.created_rows}, failed {summary.failed_rows}.
            </p>
          )}

          {rows.length > 0 && (
            <ul className="flex max-h-72 flex-col gap-2 overflow-auto">
              {rows.map((row) => (
                <li key={row.id} className="flex flex-col gap-0.5 rounded-md border p-3">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">Row {row.sheet_row_no}</span>
                    <span className="text-sm font-medium">{row.account_name}</span>
                    {row.account_type && (
                      <span className="text-xs text-muted-foreground">{tallyGroupLabel(row.account_type)}</span>
                    )}
                    {row.parent_name && (
                      <span className="text-xs text-muted-foreground">under {row.parent_name}</span>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground">{row.action}</span>
                  </div>
                  {(row.error || row.issues[0]) && (
                    <span className="text-xs text-amber-700 dark:text-amber-400">
                      {row.error ?? row.issues[0]?.message}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t pt-4">
            {summary.status === "done" ? (
              <CoButton onClick={close} disabled={busy}>
                <UploadIcon className="size-4" />
                Upload more
              </CoButton>
            ) : (
              <>
                {summary.status === "validated" && summary.invalid_rows === 0 && summary.new_rows > 0 && (
                  <CoButton onClick={create} disabled={busy || creating}>
                    {creating ? "Creating…" : `Create ${summary.new_rows} in ${books}`}
                  </CoButton>
                )}
                {summary.status === "validated" && (
                  <CoButton variant="outline" onClick={discard} disabled={busy || creating}>
                    Discard
                  </CoButton>
                )}
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md bg-muted/50 px-3 py-2">
      <span className="text-xl font-semibold tabular-nums">{value}</span>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
    </div>
  );
}
